// ...existing code...
/**
 * Negotiation Agent: Handles all negotiation workspace API communication.
 * Uses APIAgent for authenticated requests.
 */

import APIAgent from './apiAgent';

class NegotiationAPI {
  /**
   * Get a full negotiation workspace by ID.
   * @param {number} workspaceId
   * @returns {Promise<object>} Workspace data
   */
  static async getWorkspace(workspaceId) {
    return APIAgent.request(`/api/negotiations/${workspaceId}`);
  }

  /**
   * Look up a negotiation workspace by offer ID.
   * @param {number} offerId
   * @returns {Promise<object>} Workspace data
   */
  static async getWorkspaceByOffer(offerId) {
    return APIAgent.request(`/api/negotiations/by-offer/${offerId}`);
  }

  /**
   * Get timeline entries for a negotiation.
   * @param {number} workspaceId
   * @returns {Promise<Array>} Timeline entries
   */
  static async getTimeline(workspaceId) {
    return APIAgent.request(`/api/negotiations/${workspaceId}/timeline`);
  }

  /**
   * Add a comment to a negotiation workspace.
   * @param {number} workspaceId
   * @param {number} userId
   * @param {object} commentData - { content: string }
   * @returns {Promise<object>} Created comment
   */
  static async addComment(workspaceId, userId, commentData) {
    return APIAgent.request(
      `/api/negotiations/${workspaceId}/comments?user_id=${userId}`,
      'POST',
      commentData
    );
  }

  /**
   * Get current status for polling.
   * @param {number} workspaceId
   * @returns {Promise<object>} Status data
   */
  static async getStatus(workspaceId) {
    return APIAgent.request(`/api/negotiations/${workspaceId}/status`);
  }

  /**
   * Get all negotiation workspaces for a user.
   * @param {number} userId
   * @returns {Promise<Array>} List of workspaces
   */
  static async getMyWorkspaces(userId) {
    return APIAgent.request(`/api/negotiations/mine?user_id=${userId}`);
  }

  /**
   * Submit a counter-offer (uses existing offer endpoint).
   * @param {number} offerId
   * @param {object} counterData
   * @returns {Promise<object>} Counter-offer result
   */
  static async submitCounterOffer(offerId, counterData) {
    return APIAgent.request(`/api/offers/${offerId}/counter`, 'POST', counterData);
  }

  /**
   * Accept an offer (uses existing offer endpoint).
   * @param {number} offerId
   * @returns {Promise<object>} Accept result
   */
  static async acceptOffer(offerId) {
    return APIAgent.request(`/api/offers/${offerId}/accept`, 'PUT');
  }

  /**
   * Reject an offer (uses existing offer endpoint).
   * @param {number} offerId
   * @param {string} reason
   * @returns {Promise<object>} Reject result
   */
  static async rejectOffer(offerId, reason = '') {
    // Send only what the seller wrote: the buyer sees this reason (QA F15).
    const trimmed = (reason || '').trim();
    return APIAgent.request(`/api/offers/${offerId}/reject`, 'PUT', { reason: trimmed || null });
  }

  /**
   * Withdraw an offer (uses existing offer endpoint).
   * @param {number} offerId
   * @param {string} reason
   * @returns {Promise<object>} Withdraw result
   */
  static async withdrawOffer(offerId, reason = '') {
    return APIAgent.request(`/api/offers/${offerId}/withdraw`, 'PUT', { reason: reason || 'Changed mind' });
  }
}

export default NegotiationAPI;
